import { getApiUrl } from '../config';
import { CreateRecordFormData } from '../pages/CreateRecordModal';
import { AuthService } from '../services/AuthService';
import { BooleanResult } from '../types/BooleanResult';
import { CreateUser } from '../types/CreateUser';
import { CustomError } from '../types/CustomError';
import { PaginationParameters } from '../types/InputParameters';
import { NotAuthorizedError } from '../types/NotAuthorizedError';
import { Role } from '../types/Role';
import { UpdateUser } from '../types/UpdateUser';
import { User } from '../types/User';
import { UserAddressResult } from '../types/UserAddressResult';
import { addParameter } from '../util/QueryHelpers';

// export let apiURL = '';

// export const setUrl = (input: string) => {
//   apiURL = input;
// };
type Data<T> = T;

// Define the response object
export interface ResponseObject<T> {
  message: string;
  status: string;
  data: Data<T>;
  headers?: any;
}
export type returnMailReason = {
  id: number;
  name: string;
  description: string;
};
export const createUserAddress = async (record: any): Promise<ResponseObject<any>> => {
  return await post(`UserAddress`, record);
};
export const editUserAddress = async (id: string, record: CreateRecordFormData): Promise<ResponseObject<any>> => {
  const recordWithReasonId = {
    ...record,
    reason: record.returnReason,
  };

  return await put(`UserAddress/${id}`, recordWithReasonId);
};
export const getReturnMailReason = async (): Promise<ResponseObject<returnMailReason[]>> => {
  return await get(`ReturnMailReason`);
};
export const getUserAddresses = async (
  pagination: PaginationParameters,
  startDate?: string | null,
  endDate?: string | null,
  documentTypes?: string[] | null,
  reasons?: string[] | null,
  companyNames?: string[] | null,
  states?: string[] | null,
  search?: string | null,
  sorting?: { sortByColumnName: string; asc: boolean } | null,
): Promise<ResponseObject<UserAddressResult>> => {
  const { currentPage, pageSize } = pagination;

  const body = {
    currentPage,
    pageSize,
    startDate: startDate ? startDate : null,
    endDate: endDate ? endDate : null,
    documentTypes,
    reasons,
    companyNames,
    states,
    search,
    sortColumn: sorting?.sortByColumnName,
    sortAscending: sorting?.asc,
  };

  return await post(`UserAddress/Get`, body);
};

export const downloadUserAddresses = async (
  startDate?: string | null,
  endDate?: string | null,
): Promise<ResponseObject<string>> => {
  // search parameters
  let parameters = '';

  parameters = addParameter(parameters, 'startDate', startDate);
  parameters = addParameter(parameters, 'endDate', endDate);

  return await get(`UserAddress/Download${parameters}`);
};

export const getUsers = async (): Promise<ResponseObject<User[]>> => {
  return await get(`User`);
};

export const addUser = async (user: CreateUser): Promise<ResponseObject<User>> => {
  return await post(`User`, user);
};

export const updateUser = async (id: number, user: UpdateUser): Promise<ResponseObject<void>> => {
  return await put(`User/${id}`, user);
};

export const deleteUser = async (userId: number): Promise<ResponseObject<void>> => {
  return await deleteRequest(`User/${userId}`);
};

export const setUserRole = async (userId: number, roleId: number): Promise<ResponseObject<void>> => {
  return await post(`Role/ChangeUserRole`, {
    userId,
    roleId,
  });
};

export const getAvailableRoles = async (): Promise<ResponseObject<Role[]>> => {
  return await get(`Role`);
};

export const getMyRole = async (): Promise<ResponseObject<Role>> => {
  return await get(`Role/MyRole`);
};

export const resendActivationEmail = async (userId: number): Promise<ResponseObject<void>> => {
  return await post(`User/ResendActivationEmail/${userId}`, null);
};

const put = async <T>(route: string, body: any): Promise<ResponseObject<T>> => {
  const apiURL = await getApiUrl();
  const url = apiURL + route;
  const token = await AuthService.getAccessToken();

  return await fetch(url, {
    method: 'PUT',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      await validateResponse(response);

      if (!response.ok) {
        console.log(response);
        throw new Error();
      }
      return {
        status: 'Success',
        message: 'success',
        data: (await response.body?.getReader().read()) as any,
      };
    })

    .catch((error) => {
      console.error(error);
      if (error instanceof NotAuthorizedError) {
        throw error;
      } else if (error instanceof CustomError) {
        throw error;
      } else {
        throw new Error('Something went wrong - Please refresh and try again');
      }
    });
};

export const get = async (route: string) => {
  const apiURL = await getApiUrl();
  const url = apiURL + route;
  const token = await AuthService.getAccessToken();

  console.log(`API GET at URL: ${url}`);

  return await fetch(url, {
    method: 'GET',
    mode: 'cors',
    // headers,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
    .then(async (response) => {
      await validateResponse(response);

      const headers = response.headers;
      const contentType = response.headers.get('content-type');

      let data;
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('spreadsheet') ||
        contentType?.includes('application/pdf')
      ) {
        const a = document.createElement('a');
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        a.href = urlBlob;
        let filename = 'Report';
        // Extract filename from header
        const contentDisposition = headers.get('content-disposition');

        if (contentDisposition != null) {
          const parts = contentDisposition.split(';');
          const filenamePart = parts.find((n) => n.includes('filename='));
          if (filenamePart != null) {
            filename = filenamePart
              .replace('filename=', '')
              .trim()
              .replace(/^"+|"+$/g, '');
          }
        }

        a.download = filename;
        document.body.appendChild(a); // We need to append the element to the dom otherwise it will not work in firefox
        a.click();
        a.remove(); // Afterwards we remove the element again

        data = URL.createObjectURL(blob);
      } else if (contentType?.includes('image')) {
        const blob = await response.blob();
        data = URL.createObjectURL(blob);
      } else if (contentType?.includes('text/plain')) {
        data = await response.text();
      } else {
        data = await response.json();
      }
      return {
        status: 'Success',
        message: 'success',
        data,
        headers: response.headers,
      };
    })
    .catch((error) => {
      console.error(error);
      if (error instanceof NotAuthorizedError) {
        throw error;
      } else if (error instanceof CustomError) {
        throw error;
      } else {
        throw new Error('Something went wrong - Please refresh and try again');
      }
    });
};

const post = async <T>(route: string, body: any): Promise<ResponseObject<T>> => {
  const apiURL = await getApiUrl();
  const url = apiURL + route;
  const token = await AuthService.getAccessToken();

  return await fetch(url, {
    method: 'POST',
    mode: 'cors',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : null,
  })
    .then(async (response) => {
      await validateResponse(response);

      const headers = response.headers;
      const contentType = response.headers.get('content-type');

      if (contentType == null) {
        return {
          status: 'Success',
          message: 'success',
          data: (await response.body?.getReader().read()) as any,
        };
      }

      let data;
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('spreadsheet') ||
        contentType?.includes('application/pdf')
      ) {
        const a = document.createElement('a');
        a.href = url;
        let filename = 'Report';
        // Extract filename from header
        const contentDisposition = headers.get('content-disposition');

        if (contentDisposition != null) {
          const parts = contentDisposition.split(';');
          const filenamePart = parts.find((n) => n.includes('filename='));
          if (filenamePart != null) {
            filename = filenamePart
              .replace('filename=', '')
              .trim()
              .replace(/^"+|"+$/g, '');
          }
        }

        a.download = filename;
        document.body.appendChild(a); // We need to append the element to the dom otherwise it will not work in firefox
        a.click();
        a.remove(); // Afterwards we remove the element again

        const blob = await response.blob();
        data = URL.createObjectURL(blob);
      } else if (contentType?.includes('image')) {
        const blob = await response.blob();
        data = URL.createObjectURL(blob);
      } else if (contentType?.includes('text/plain')) {
        data = await response.text();
      } else {
        data = await response.json();
      }
      return {
        status: 'Success',
        message: 'success',
        data,
        headers: response.headers,
      };
    })
    .catch((error) => {
      console.error(error);
      if (error instanceof NotAuthorizedError) {
        throw error;
      } else if (error instanceof CustomError) {
        throw error;
      } else {
        throw new Error('Something went wrong');
      }
    });
};

const deleteRequest = async <T>(route: string): Promise<ResponseObject<T>> => {
  const apiURL = await getApiUrl();
  const url = apiURL + route;
  const token = await AuthService.getAccessToken();

  return await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  })
    .then(async (response) => {
      await validateResponse(response);

      if (!response.ok) {
        console.log(response);
        throw new Error();
      }
      return {
        status: 'Success',
        message: 'success',
        data: (await response.body?.getReader().read()) as any,
      };
    })

    .catch((error) => {
      console.error(error);
      if (error instanceof NotAuthorizedError) {
        throw error;
      } else if (error instanceof CustomError) {
        throw error;
      } else {
        throw new Error('Something went wrong - Please refresh and try again');
      }
    });
};

const validateResponse = async (response: Response) => {
  if (!response.ok) {
    console.log(`fetch response status: ${response.status}`);
  }
  if (response.status == 403) {
    throw new NotAuthorizedError('Action is not authorized. Please request additional permissions as necessary.');
  }
  if (response.status == 418) {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('text/plain')) {
      const data = await response.text();

      if (data != null && data !== '') {
        throw new CustomError(data);
      }
    }
  }
  if (!response.ok) {
    throw new Error('Failed to fetch');
  }
};
